/**
 * What is on a receipt, as data.
 *
 * The vocabulary only: the blocks a slip is made of, the order and branch it is
 * built from, and the catalogue of strings it is written in. **The arrangement
 * moved to `template.ts`** when it became a document an operator edits — this
 * file is what a receipt *can* say, that one is what a given chain says.
 *
 * The renderers are tested against these blocks separately, so a layout change
 * still needs no printer.
 */
import type { Money } from '@mgc/domain'

export type Align = 'left' | 'center' | 'right'

/**
 * The three sizes the design is drawn in — 4.5, 6 and 12 points — as ratios of
 * the body size rather than as points, because the body size is the renderer's
 * to choose from the paper it was given.
 */
export type TextSize = 'small' | 'normal' | 'large'

/** A one-bit picture, already dithered by whoever prepared it. */
export interface SlipImage {
  /** A `data:image/png;base64,…` URL. */
  readonly data: string
  /** The printable width it was prepared for, in dots. */
  readonly dots: number
  /** Its height at that width, known without decoding it. */
  readonly height: number
}

export type SlipBlock =
  | { kind: 'text'; text: string; align?: Align; bold?: boolean; size?: TextSize }
  /** A left label and a right amount, filled to the paper width. */
  | { kind: 'row'; left: string; right: string; bold?: boolean; indent?: number }
  | { kind: 'rule'; char?: string }
  | { kind: 'space'; lines?: number }
  | { kind: 'qr'; data: string; caption?: string }
  /**
   * The masthead: the brand art on the left and a few short lines — a phone
   * number, a website — set small against the right margin.
   *
   * The image's size is known without decoding it, which is what lets
   * `measureHeight` reserve the space whether or not the picture arrives. On a
   * head too narrow for both side by side, the lines drop under the art.
   */
  | { kind: 'header'; image: SlipImage | null; lines: readonly string[] }
  | { kind: 'barcode'; data: string }
  /** The order number, printed as big as the design allows, after its label. */
  | { kind: 'number'; value: string; label?: string }
  /**
   * One row of the item table: name, quantity, unit price, amount.
   *
   * Four cells rather than a left and a right, because the design sets them in
   * columns — and a column is the one thing padding a `row` cannot line up.
   * `head` is the table's own heading row.
   */
  | { kind: 'columns'; cells: readonly [string, string, string, string]; head?: boolean }
  /**
   * The e-Barimt QR with what a customer copies off the slip beside it.
   *
   * `qr` is null when there is no code to draw; the items then print on their
   * own rather than beside a gap.
   */
  | { kind: 'fiscalPanel'; qr: string | null; items: readonly { readonly label: string; readonly value: string }[] }

export interface Slip {
  readonly blocks: readonly SlipBlock[]
  /** Feed and cut after printing. False for a preview. */
  readonly cut: boolean
}

export interface SlipOrder {
  readonly displayNumber: string
  readonly orderType: 'eat_in' | 'take_out'
  readonly placedAt: string
  readonly gross: Money
  readonly net: Money
  readonly vat: Money
  readonly savings: Money
  readonly change: Money
  readonly lines: readonly {
    readonly name: string
    readonly quantity: number
    readonly total: Money
    readonly note: string | null
    readonly addOns: readonly { label: string; quantity: number; total: Money }[]
  }[]
  readonly payments: readonly { readonly method: string; readonly amount: Money; readonly reference: string | null }[]
  readonly company: { readonly registrationNumber: string; readonly name: string } | null
  readonly fiscal: {
    readonly lottery: string | null
    readonly billId: string | null
    readonly qrData: string | null
    /**
     * 1C's check number, which the slip prints as the order number when it
     * has one. Null until the filing lands — offline, queued, refused — and
     * then `displayNumber` is printed instead.
     */
    readonly onecNumber?: number | null
  } | null
}

export interface SlipBranch {
  readonly name: string
  readonly address: string | null
  readonly wifiName: string | null
  readonly wifiPassword: string | null
}

export interface SlipStrings {
  readonly eatIn: string
  readonly takeOut: string
  readonly subtotal: string
  readonly discount: string
  readonly vat: string
  readonly total: string
  readonly change: string
  readonly paidBy: Record<string, string>
  readonly lottery: string
  /** ДДТД — one of the three ways a customer can register the sale. */
  readonly billId: string
  readonly fiscalPending: string
  readonly company: string
  readonly wifi: string
  readonly thanks: string
  readonly orderNumber: string
  /*
   * Everything below arrived with the 2026-09 redesign, and is optional on the
   * wire: a template saved before it has none of them, and `buildSlip` fills
   * the gaps from `MN` rather than refusing a document that was valid when it
   * was written.
   */
  /** The legal entity that sells, as e-Barimt knows it. */
  readonly sellerName: string
  /** Its taxpayer number, printed opposite its name. */
  readonly sellerTin: string
  readonly branch: string
  readonly branchAddress: string
  readonly date: string
  readonly product: string
  readonly quantity: string
  readonly unitPrice: string
  readonly amount: string
  /** What the customer handed over, in total, across every payment. */
  readonly paid: string
  readonly paymentMethod: string
  /** The amount e-Barimt registered, printed large beside the lottery number. */
  readonly ebarimtAmount: string
  readonly wifiPassword: string
}

export const MN: SlipStrings = {
  eatIn: 'Эндээ',
  takeOut: 'Авч явах',
  subtotal: 'Захиалгын дүн',
  discount: 'Хөнгөлөлт',
  vat: 'НӨАТ',
  total: 'Нийт дүн',
  change: 'Хариулт',
  paidBy: { cash: 'Бэлэн мөнгө', card: 'Банкны карт', qpay: 'QPay' },
  lottery: 'Сугалааны дугаар',
  billId: 'ДДТД',
  // The receipt prints without a lottery number when the filing has not
  // happened yet, and says so rather than leaving a blank line the customer
  // reads as a missing tax receipt.
  fiscalPending: 'И-баримт хүлээгдэж байна',
  company: 'Байгууллага',
  wifi: 'Wifi',
  thanks: 'Mega Mgc Coffee-г сонгон үйлчлүүлсэнд баярлалаа.',
  orderNumber: 'Захиалгын дугаар',
  sellerName: 'Ази Брю Хаус ХХК',
  sellerTin: '7006417',
  branch: 'Салбар',
  branchAddress: 'Салбарын хаяг',
  date: 'Огноо',
  product: 'Бүтээгдэхүүний нэр',
  quantity: 'Тоо',
  unitPrice: 'Үнэ',
  amount: 'Дүн',
  paid: 'Төлбөрийн дүн',
  paymentMethod: 'Төлбөрийн хэрэгсэл',
  ebarimtAmount: 'Ebarimt-ын дүн',
  wifiPassword: 'Password'
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * When the order was placed, as the customer reads it — `YYYY/MM/DD`, the way
 * the design writes it.
 *
 * Local time and not the branch's timezone: the machine printing this is the
 * one standing in the shop. Exported because `template.ts` builds the slip, and
 * deliberately not `lib/format.ts`'s — that one is the console's.
 */
export const placedOnLabel = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`
}

/** The same moment to the second, for the footer. */
export const placedAtLabel = (iso: string): string => {
  const d = new Date(iso)
  return `${placedOnLabel(iso)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}
